import { TeamAPI } from "../../../src/apis/TeamAPI"
import { GetStaticPropsContext } from "next"
import { Col, Row } from "react-bootstrap"
import React from "react"
import { TeamDetails } from "@components/Team/TeamDetails/TeamDetails"
import { getNavigationPropsForTeam } from "@components/Navigation/getNavigationProps"
import { SEO } from "../../../src/types/next.js"
import moment from "moment"
import { ParsedUrlQuery } from "querystring"
import InferNextPropsType from "infer-next-props-type"
import * as NextConstants from "../../../src/constants/next"
import { TeamMenu } from "@components/Team/TeamMenu/TeamMenu"
import { getTeamCanonicalData } from "utils/team"
import { orderBy } from "lodash"
import { removeLinkRelNoopenerNoreferrer } from "../../../src/utils/urls"
import { NewTitleBanner } from "@components/Banners/NewTitleBanner"
import homeTeamStats from "@assets/images/title-banner/home-team-stats.png"
import { TurnMatches } from "@components/Turn/TurnMatches"
import { MatchBettingStats } from "@components/Match/MatchBettingStats"
import { TeamBettingStatsWithParameterFlat } from "../../../src/types/teamBettingStats"

export default function TeamPage({
  team,
  teamCompetitionSeasonsStats,
  nextMatches,
  homeTeamBettingStats,
  awayTeamBettingStats,
}: InferNextPropsType<typeof getStaticProps>) {
  return (
    <Row className="paddingZero">
      <Row className={"marginZero"}>
        <Col md={12} className={"paddingZero"}>
          <aside id="hs-sidebar" className={"hs-border-div paddingZero"}>
            <TeamDetails team={team} />
          </aside>
        </Col>
      </Row>
      <TeamMenu
        team={team}
        teamCompetitionSeasonsStats={teamCompetitionSeasonsStats}
        selected="statistiche"
        teamCalciocomSlug={team.calciocomSlug!}
        title={`Statistiche ${team.name}`}
      />
      {nextMatches.length > 0 && nextMatches[0] && homeTeamBettingStats && awayTeamBettingStats && (
        <>
          <Row className="marginZero">
            <NewTitleBanner icon={homeTeamStats} title="STATISTICHE PROSSIMA PARTITA" />
          </Row>
          <Row>
            <MatchBettingStats
              match={nextMatches[0]}
              homeTeamBettingStats={homeTeamBettingStats}
              awayTeamBettingStats={awayTeamBettingStats}
              isH1={false}
              newTitleBanner={false}
            />
          </Row>
        </>
      )}
      {nextMatches.length > 0 && (
        <>
          <Row>
            <NewTitleBanner icon={homeTeamStats} title="PROSSIME PARTITE" />
          </Row>
          <Row>
            <TurnMatches matches={nextMatches} loading={false} />
          </Row>
        </>
      )}

      {team.description ? (
        <Row className="team-description-container">
          <div className="team-description">
            <div className="teamDescriptionContainer">
              <div
                className="teamDescription"
                dangerouslySetInnerHTML={{ __html: removeLinkRelNoopenerNoreferrer(team.description) }}
              ></div>
            </div>
          </div>
        </Row>
      ) : null}
    </Row>
  )
}

export async function getStaticPaths() {
  return { paths: [], fallback: "blocking" }
}

interface IParams extends ParsedUrlQuery {
  teamCalciocomSlug: string
  competitionCalciocomSlug: string
  seasonCalciocomSlug: string
}

export async function getStaticProps(context: GetStaticPropsContext<IParams>) {
  try {
    const { teamCalciocomSlug } = context.params!

    const [teamIds] = await Promise.all([TeamAPI.getTeamIds(teamCalciocomSlug)])
    const [team, teamIndexStats, nextMatches, navigationProps] = await Promise.all([
      TeamAPI.getTeam(teamIds.id),
      TeamAPI.getTeamIndexStats(teamIds.id),
      TeamAPI.getNextMatches(teamIds.id),
      getNavigationPropsForTeam(teamIds.id),
    ])

    let teamCompetitionSeasonsStats = await TeamAPI.getTeamCompetitionSeasonStats(teamIds.id)
    teamCompetitionSeasonsStats = orderBy(teamCompetitionSeasonsStats, (item) => item.competitionSeason.season?.year, [
      "desc",
    ])

    let homeTeamBettingStats: TeamBettingStatsWithParameterFlat | null = null
    let awayTeamBettingStats: TeamBettingStatsWithParameterFlat | null = null
    const match = nextMatches[0]
    if (match) {
      ;[homeTeamBettingStats, awayTeamBettingStats] = await Promise.all([
        match.homeTeam ? await TeamAPI.getBettingStats(match.homeTeam.id, match.competitionSeasonId) : null,
        match.awayTeam ? await TeamAPI.getBettingStats(match.awayTeam.id, match.competitionSeasonId) : null,
      ])
    }

    const title = team.metaTitle || `${team.completeName}: Profilo squadra e Statistiche`
    const seo: SEO = {
      description:
        team.metaDescription ||
        `Scopri le statistiche di ${team.completeName} nelle partite di Campionato e nelle coppe internazionali. Vittorie, gol, possesso palla, falli e tanto altro`,
      keywords: [
        `${team.name}`,
        "calcio",
        moment().year().toString(),
        "Rosa",
        "Calendario",
        "Club",
        "Squadra",
        "informazioni",
        "Statistiche",
      ],
      ...(await getTeamCanonicalData(team, null, `/squadre/${teamCalciocomSlug}`)),
      index: true,
      follow: true,
    }

    return {
      props: {
        team,
        teamCompetitionSeasonsStats,
        teamIndexStats,
        navigationProps,
        seo,
        title,
        nextMatches,
        notFound: false,
        homeTeamBettingStats,
        awayTeamBettingStats,
      },
      revalidate: NextConstants.REVALIDATE,
    }
  } catch (error) {
    console.error(error)
    return {
      notFound: true,
    }
  }
}
